import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import strings from "../../i18n/language";
import { getRatingsAction } from "../../redux/actions/rating";

const BRAND = '#3b95e8';
const GOLD = '#ffc107';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';
const TEXT = '#e8f0fe';

const COLUMNS = 8;
const LOW_RATING = 3;

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

const TYPE_LABELS = { apple: 'Apple', google: 'Google', local: 'Local', web: 'Web' };

const FilterChip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? 'rgba(59,149,232,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(59,149,232,0.5)' : BORDER}`,
    borderRadius: 8,
    color: active ? BRAND : MUTED,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    letterSpacing: 0.2,
  }}>
    {label}
  </button>
);

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

const Stars = ({ rate, size = 16 }) => (
  <span style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}>
    {[1, 2, 3, 4, 5].map((n) => (n <= rate
      ? <StarRoundedIcon key={n} style={{ fontSize: size, color: GOLD }} />
      : <StarOutlineRoundedIcon key={n} style={{ fontSize: size, color: 'rgba(255,255,255,0.15)' }} />
    ))}
  </span>
);

// Average, count, one bar per score and the average per account type
const Summary = ({ summary }) => {
  const max = Math.max(1, ...Object.values(summary.distribution));
  return (
    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center', padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
      <div style={{ minWidth: 140 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {strings.LABEL_RATING_AVERAGE}
        </div>
        <div style={{ fontSize: 40, fontWeight: 800, color: TEXT, lineHeight: 1.1 }}>
          {summary.average ?? '—'}
        </div>
        <Stars rate={Math.round(summary.average || 0)} size={18} />
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
          {summary.count} {strings.LABEL_RATING_TOTAL}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        {[5, 4, 3, 2, 1].map((score) => {
          const count = summary.distribution[score] || 0;
          const share = summary.count ? Math.round((count / summary.count) * 100) : 0;
          return (
            <div key={score} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ width: 14, fontSize: 12, color: MUTED, textAlign: 'right' }}>{score}</span>
              <StarRoundedIcon style={{ fontSize: 14, color: GOLD }} />
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }}>
                <div style={{ width: `${(count / max) * 100}%`, height: '100%', borderRadius: 4, background: score <= LOW_RATING ? '#e05c5c' : GOLD }} />
              </div>
              <span style={{ width: 70, fontSize: 12, color: MUTED }}>{count} · {share}%</span>
            </div>
          );
        })}
      </div>
      <div style={{ minWidth: 160 }}>
        {summary.byType.map((t) => (
          <div key={t.type} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, color: MUTED, marginBottom: 6 }}>
            <span>{TYPE_LABELS[t.type] || t.type}</span>
            <span style={{ color: TEXT, fontWeight: 600 }}>{t.average} <span style={{ color: MUTED, fontWeight: 400 }}>({t.count})</span></span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Web accounts sign in with their email; Google and Apple accounts can be
// looked up in Firebase by their id; device-only accounts have no contact
const Contact = ({ rating }) => {
  if (rating.email) {
    return <a href={`mailto:${rating.email}`} style={{ color: BRAND, textDecoration: 'none' }}>{rating.email}</a>;
  }
  if (rating.type === 'google' || rating.type === 'apple') {
    return (
      <Tooltip title={strings.LABEL_RATING_FIREBASE} placement="left">
        <span style={{ fontFamily: 'monospace', fontSize: 11, userSelect: 'all', wordBreak: 'break-all' }}>{rating.id}</span>
      </Tooltip>
    );
  }
  return <span style={{ color: MUTED }}>{strings.LABEL_RATING_NO_CONTACT}</span>;
};

const FILTERS = [
  { value: null, label: () => strings.LABEL_RATING_ALL },
  { value: 'low', label: () => strings.LABEL_RATING_LOW },
  ...[5, 4, 3, 2, 1].map((n) => ({ value: n, label: () => `${n} ★` })),
];

const matches = (filter, rate) => {
  if (filter === null) return true;
  if (filter === 'low') return rate <= LOW_RATING;
  return rate === filter;
};

const RatingList = () => {
  const [filter, setFilter] = useState(null);
  const dispatch = useDispatch();

  const summary = useSelector((state) => state.rating.summary);
  const ratings = useSelector((state) => state.rating.ratings);
  const isLoading = useSelector((state) => state.rating.isLoadingGetRatings);

  useEffect(() => {
    dispatch(getRatingsAction());
  }, [dispatch]);

  const shown = ratings.filter((r) => matches(filter, r.rate));

  return (
    <Box sx={{ width: "100%" }}>
      <div style={card}>

        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <Typography style={{ fontSize: 15, fontWeight: 700, color: TEXT, letterSpacing: 0.2 }}>
            {strings.LABEL_RATING_TITLE}
          </Typography>
        </div>

        {summary && <Summary summary={summary} />}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '16px 24px', borderBottom: `1px solid ${BORDER}` }}>
          {FILTERS.map((f) => (
            <FilterChip
              key={f.value ?? 'all'}
              label={f.label()}
              active={filter === f.value}
              onClick={() => setFilter(f.value)}
            />
          ))}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_RATING_DATE}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_NOTE}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_CUSTOMER}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_ACCOUNT}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_SHOPS}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_LAST_VISIT}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_CONNECTIONS}</ColHeader>
                <ColHeader>{strings.LABEL_RATING_CONTACT}</ColHeader>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={COLUMNS} style={{ textAlign: 'center', padding: 40 }}>
                    <CircularProgress size={24} sx={{ color: BRAND }} />
                  </td>
                </tr>
              )}
              {!isLoading && shown.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS} style={{ textAlign: 'center', padding: 40, fontSize: 13, color: MUTED }}>
                    {strings.LABEL_RATING_EMPTY}
                  </td>
                </tr>
              )}
              {!isLoading && shown.map((rating) => (
                <tr key={rating.id}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Td style={{ color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {rating.ratedAt ? dayjs(rating.ratedAt).format("DD/MM/YYYY HH:mm") : strings.LABEL_RATING_NO_DATE}
                  </Td>
                  <Td><Stars rate={rating.rate} /></Td>
                  <Td>
                    <div style={{ color: TEXT }}>{rating.name || '—'}</div>
                    {rating.staffOf && (
                      <div style={{ fontSize: 11, color: BRAND, marginTop: 2 }}>
                        {strings.LABEL_RATING_STAFF} · {rating.staffOf}
                      </div>
                    )}
                  </Td>
                  <Td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {TYPE_LABELS[rating.type] || rating.type}
                    {rating.language && <span style={{ color: MUTED }}> · {rating.language}</span>}
                  </Td>
                  <Td style={{ fontSize: 12 }}>
                    {rating.shops.length ? rating.shops.join(', ') : <span style={{ color: MUTED }}>—</span>}
                  </Td>
                  <Td style={{ color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {rating.lastVisit ? dayjs(rating.lastVisit).format("DD/MM/YYYY") : strings.LABEL_RATING_NEVER}
                  </Td>
                  <Td style={{ fontSize: 12 }}>{rating.connections ?? 0}</Td>
                  <Td style={{ fontSize: 12, maxWidth: 260 }}><Contact rating={rating} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Box>
  );
};

export default RatingList;
